import React, { useState, useEffect } from "react";
import { Spin, Input, Select } from "antd";
import {
  EnvironmentOutlined,
  SearchOutlined,
  SendOutlined,
  HomeOutlined,
} from "@ant-design/icons";
import api, { extractFireDACData } from "../services/api";

const DARK_NAV = "#000000";
const ORANGE   = "#f5a623";
const MOBILE_BREAKPOINT = 768;

// ── Responsividade (mesmo padrão do Dashboard) ─────────────────────────────
function useIsMobile(breakpoint = MOBILE_BREAKPOINT) {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= breakpoint
  );
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", handler);
    handler();
    return () => window.removeEventListener("resize", handler);
  }, [breakpoint]);
  return isMobile;
}

// ── Normaliza chaves para lowercase (FireDAC às vezes varia o case) ─────────
const normalize = (row) => {
  if (!row || typeof row !== "object") return {};
  const out = {};
  for (const [k, v] of Object.entries(row)) out[k.toLowerCase()] = v;
  return out;
};


const isDataVazia = (val) => {
  if (val === null || val === undefined) return true;
  const s = String(val).trim();
  if (s === "" || s === "null" || s === "undefined" || s === "0") return true;
  // Delphi zero-date
  if (s.includes("1899-12-30") || s.includes("18991230")) return true;
  return false;
};

// ── Formata TSQLTimeStamp -> DD/MM/YYYY ─────────────────────────────────────
// FireDAC costuma serializar como "2025-07-21T00:00:00" ou "2025-07-21 00:00:00.000" ou "20270102T000000.000"
const formatData = (val, vazio = "—") => {
  if (isDataVazia(val)) return vazio;
  const s = String(val);

  // Tenta extrair YYYY-MM-DD
  let m = s.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;

  // Tenta extrair YYYYMMDD (formato sem hífens)
  m = s.match(/(\d{4})(\d{2})(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;

  // Fallback: tentar com Date
  const d = new Date(s);
  if (!isNaN(d.getTime())) return d.toLocaleDateString("pt-BR");

  return s;
};

// Chave "ordenável" da data (YYYYMMDD) para ordenar do mais antigo ao mais recente.
const dataSortKey = (val) => {
  if (isDataVazia(val)) return "00000000";
  const s = String(val);

  // Tenta extrair YYYY-MM-DD
  let m = s.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}${m[2]}${m[3]}`;

  // Tenta extrair YYYYMMDD (formato sem hífens)
  m = s.match(/(\d{4})(\d{2})(\d{2})/);
  if (m) return `${m[1]}${m[2]}${m[3]}`;

  return "00000000";
};

export default function VooHotel({ userId, isCliente }) {
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [view, setView]       = useState("voos");     // "voos" | "hoteis"
  const [search, setSearch]   = useState("");
  const [filtroCliente, setFiltroCliente] = useState(null);

  const [voos, setVoos]       = useState([]);
  const [hoteis, setHoteis]   = useState([]);
  const [clientes, setClientes] = useState([]);       // alvos (para o filtro do gestor)

  // Só o gestor vê a coluna/filtro de cliente. O cliente vê só o que é dele.
  const showCliente = !isCliente;

  useEffect(() => {
    if (!userId) return;
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, isCliente]);

  const carregar = async () => {
    setLoading(true);
    try {

      let alvos = [];
      if (isCliente) {
        // Cliente: userId já é o cli_id
        const nomeCliente = localStorage.getItem("userName") || "Você";
        alvos = [{ id: userId, nome: nomeCliente }];
      } else {
        const r = await api.get("/ServerPrincipal/PesquisaClientes", { params: { Login: userId } });
        alvos = extractFireDACData(r.data).map((c) => ({
          id:   c.cli_id || c.id,
          nome: c.cli_nome || c.nome || "Cliente",
        }));
      }
      setClientes(alvos);

      // 2. Busca voos e hotéis de cada alvo, em paralelo. Falha de um cliente
      //    não derruba os demais.
      const voosAll = [];
      const hoteisAll = [];

      await Promise.all(
        alvos.map(async (alvo) => {
          try {
            const rv = await api.get("/ServerPrincipal/PesquisaVoo", { params: { Cliente: alvo.id } });
            extractFireDACData(rv.data).map(normalize).forEach((row) => {
              voosAll.push({
                clienteId:   alvo.id,
                clienteNome: alvo.nome,
                localizador: row.ven_localizador || "",
                origem:      row.ven_origem || "",
                destino:     row.ven_destino || "",
                programa:    row.car_nome_programa || "",
                data:        row.ven_data_voo || "",
                dataVolta:   row.ven_data_voo_volta ?? "",
              });
            });
          } catch (e) {
            console.warn(`[Voos] cliente ${alvo.id}:`, e);
          }

          try {
            const rh = await api.get("/ServerPrincipal/PesquisaHotel", { params: { Cliente: alvo.id } });
            extractFireDACData(rh.data).map(normalize).forEach((row) => {
              hoteisAll.push({
                clienteId:   alvo.id,
                clienteNome: alvo.nome,
                voucher:     row.hos_voucher_reserva || "",
                hotel:       row.hos_nome_hospedagem || "",
                checkin:     row.hos_data_check_in || "",
                checkout:    row.hos_data_checkout || "",
              });
            });
          } catch (e) {
            console.warn(`[Hotéis] cliente ${alvo.id}:`, e);
          }
        })
      );

      // Mais antigos primeiro (2026 → 2027).
      voosAll.sort((a, b) => dataSortKey(a.data).localeCompare(dataSortKey(b.data)));
      hoteisAll.sort((a, b) => dataSortKey(a.checkin).localeCompare(dataSortKey(b.checkin)));

      setVoos(voosAll);
      setHoteis(hoteisAll);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // ── Configuração das colunas (dirige tabela desktop e cards mobile) ───────
  const colunasVoos = [
    showCliente && { key: "clienteNome", label: "Cliente",     flex: "1.3fr" },
    { key: "localizador", label: "Localizador", flex: "1fr" },
    { key: "trecho",      label: "Trecho",      flex: "1.5fr", render: (r) => `${r.origem || "—"}` },
    { key: "programa",    label: "Programa",    flex: "1.1fr" },
    { key: "data",        label: "Ida",         flex: "0.9fr", render: (r) => formatData(r.data) },

    {
      key: "dataVolta",
      label: "Volta",
      flex: "0.9fr",
      render: (r) =>
        isDataVazia(r.dataVolta)
          ? <span style={{ color: "#bbb" }}>Só ida</span>
          : formatData(r.dataVolta),
    },
  ].filter(Boolean);

  const colunasHoteis = [
    showCliente && { key: "clienteNome", label: "Cliente",  flex: "1.4fr" },
    { key: "voucher", label: "Reserva",  flex: "1.2fr" },
    { key: "hotel",    label: "Hotel",     flex: "2fr" },
    { key: "checkin",  label: "Check-in",  flex: "1fr", render: (r) => formatData(r.checkin) },
    { key: "checkout", label: "Check-out", flex: "1fr", render: (r) => formatData(r.checkout) },
  ].filter(Boolean);

  const colunas = view === "voos" ? colunasVoos : colunasHoteis;


  const minWidthTabela = view === "voos" ? 780 : 760;

  // ── Filtro de busca + filtro por cliente ──────────────────────────────────
  const aplicaFiltros = (linhas, campos) =>
    linhas.filter((r) => {
      if (filtroCliente && r.clienteId !== filtroCliente) return false;
      if (!search) return true;
      const t = search.toLowerCase();
      return campos.some((c) => String(r[c] || "").toLowerCase().includes(t));
    });

  const dados =
    view === "voos"
      ? aplicaFiltros(voos, ["localizador", "origem", "destino", "programa", "clienteNome"])
      : aplicaFiltros(hoteis, ["voucher", "hotel", "clienteNome"]);

  const gridCols = colunas.map((c) => c.flex).join(" ");
  const valorCelula = (col, row) => (col.render ? col.render(row) : row[col.key] || "—");

  // ── Toggle Voos / Hotéis ──────────────────────────────────────────────────
  const ToggleBtn = ({ id, icon, label, count }) => {
    const active = view === id;
    return (
      <button
        onClick={() => setView(id)}
        style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: isMobile ? "9px 14px" : "9px 18px",
          border: "none", borderRadius: 8, cursor: "pointer",
          fontSize: 14, fontWeight: 600,
          background: active ? DARK_NAV : "transparent",
          color: active ? "#fff" : "#666",
          flex: isMobile ? 1 : "initial",
          justifyContent: "center",
          transition: "all 0.15s",
        }}
      >
        {icon} {label}
        <span style={{
          background: active ? ORANGE : "#e8e8e8",
          color: active ? "#fff" : "#888",
          borderRadius: 10, fontSize: 11, fontWeight: 700,
          padding: "1px 7px", minWidth: 20, textAlign: "center",
        }}>{count}</span>
      </button>
    );
  };

  return (
    <div>
      {/* Cabeçalho */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20 }}>
        <EnvironmentOutlined style={{ fontSize: 18, color: "#555" }} />
        <span style={{ fontSize: 18, fontWeight: 600, color: "#222" }}>Voos e Hotéis</span>
      </div>

      {/* Toggle + filtros */}
      <div style={{
        background: "#fff", borderRadius: 8, border: "1px solid #e8e8e8",
        padding: isMobile ? "12px 14px" : "14px 18px", marginBottom: 16,
      }}>
        <div style={{
          display: "flex", gap: 8, background: "#f5f5f5", borderRadius: 10,
          padding: 4, marginBottom: 14,
        }}>
          <ToggleBtn id="voos"   icon={<SendOutlined />} label="Voos"   count={voos.length} />
          <ToggleBtn id="hoteis" icon={<HomeOutlined />} label="Hotéis" count={hoteis.length} />
        </div>

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div style={{ flex: 1, minWidth: isMobile ? "100%" : 240 }}>
            <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>
              {view === "voos" ? "Buscar por localizador, trecho ou programa" : "Buscar por reserva ou hotel"}
            </div>
            <Input
              placeholder="Digite para filtrar..."
              prefix={<SearchOutlined style={{ color: "#bbb" }} />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
            />
          </div>

          {showCliente && (
            <div style={{ minWidth: isMobile ? "100%" : 220, flex: isMobile ? "1 1 100%" : "initial" }}>
              <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>Cliente</div>
              <Select
                placeholder="Todos os clientes"
                allowClear
                style={{ width: "100%" }}
                value={filtroCliente}
                onChange={setFiltroCliente}
                showSearch
                optionFilterProp="children"
              >
                {clientes.map((c) => (
                  <Select.Option key={c.id} value={c.id}>{c.nome}</Select.Option>
                ))}
              </Select>
            </div>
          )}
        </div>
      </div>

      {/* Tabela / Cards */}
      <div style={{
        background: "#fff", borderRadius: 8, border: "1px solid #e8e8e8",
        // "auto" (era "hidden"): com a coluna nova, telas estreitas precisam
        // de scroll horizontal — senão a coluna Volta é simplesmente cortada.
        overflow: isMobile ? "visible" : "auto",
      }}>
        {/* Cabeçalho da tabela (só desktop) */}
        {!isMobile && (
          <div style={{
            display: "grid", gridTemplateColumns: gridCols,
            padding: "10px 20px", background: "#fafafa",
            borderBottom: "1px solid #f0f0f0", minWidth: minWidthTabela,
          }}>
            {colunas.map((c) => (
              <div key={c.key} style={{ fontSize: 12, fontWeight: 600, color: "#888" }}>{c.label}</div>
            ))}
          </div>
        )}

        <Spin spinning={loading}>
          {dados.length === 0 && !loading ? (
            <div style={{ textAlign: "center", padding: "48px 0", color: "#bbb", fontSize: 14 }}>
              {view === "voos" ? "Nenhum voo encontrado" : "Nenhuma hospedagem encontrada"}
            </div>
          ) : isMobile ? (
            // ── Cards no mobile ──
            dados.map((row, idx) => (
              <div key={idx} style={{ padding: "14px 16px", borderBottom: "1px solid #f5f5f5" }}>
                {colunas.map((c) => (
                  <div key={c.key} style={{
                    display: "flex", justifyContent: "space-between", gap: 12,
                    padding: "3px 0", fontSize: 13,
                  }}>
                    <span style={{ color: "#999", fontWeight: 500, flexShrink: 0 }}>{c.label}</span>
                    <span style={{ color: "#333", fontWeight: 600, textAlign: "right", wordBreak: "break-word" }}>
                      {valorCelula(c, row)}
                    </span>
                  </div>
                ))}
              </div>
            ))
          ) : (
            // ── Grid no desktop ──
            dados.map((row, idx) => (
              <div key={idx}
                style={{
                  display: "grid", gridTemplateColumns: gridCols,
                  padding: "12px 20px", borderBottom: "1px solid #f5f5f5",
                  alignItems: "center", minWidth: minWidthTabela,
                  background: idx % 2 === 0 ? "#fff" : "#fafcff",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#f0f7ff")}
                onMouseLeave={(e) => (e.currentTarget.style.background = idx % 2 === 0 ? "#fff" : "#fafcff")}
              >
                {colunas.map((c) => (
                  <div key={c.key} style={{
                    fontSize: 13, color: "#333",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    paddingRight: 8,
                  }}>
                    {valorCelula(c, row)}
                  </div>
                ))}
              </div>
            ))
          )}
        </Spin>
      </div>
    </div>
  );
}